import { useState } from "react";
import { Button } from "@jhool-io/fe-components";
import { useAppSelector } from "../../../../hooks/useRedux";
import useToast from "../../../../hooks/useToast";
import ArrowUpRightIcon from "../../../../icons/ArrowUpRight";
import InfoIcon from "../../../../icons/Info";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import { ISessionNote } from "../../../notes/types/notes.types";
import useCanViewEPrescribeFlow from "../../hooks/useCanViewEPrescribeFlow";
import {
    useTriggerClientSync,
    useTriggerSSOLaunch,
} from "../../hooks/e-prescribe.mutations";
import {
    useGetClientAllergies,
    useGetClientMedications,
    useGetPrescriptions,
} from "../../hooks/e-prescribe.queries";
import {
    ISSOLaunchResponse,
    SSOLandingScreens,
} from "../../types/e-prescribe.types";
import ClientAllergyListTable from "../ClientAllergyList/ClientAllergyListTable";
import ClientMedicationListTable from "../ClientMedicationList/ClientMedicationListTable";
import DrFirstIframe from "../DrFirstIframe/DrFirstIframe";
import PrescriptionListTable from "../PrescriptionList/PrescriptionListTable";

interface MedNoteEPrescribeProps {
    noteDetails: ISessionNote;
    showDiagnosisSync?: boolean;
}

export default function MedNoteEPrescribe({
    noteDetails,
    showDiagnosisSync = false,
}: MedNoteEPrescribeProps) {
    const [launchData, setLaunchData] = useState<ISSOLaunchResponse | null>(
        null
    );
    const { practice } = useAppSelector((state) => state.userPractice);
    const canViewEPrescribeFlow = useCanViewEPrescribeFlow();
    const { toast } = useToast();
    const practiceId = practice?.practice_id ?? "";
    const clientId = noteDetails.client.client_id;

    const canFetchEPrescribeData = Boolean(
        practiceId && clientId && canViewEPrescribeFlow
    );

    const clientMedications = useGetClientMedications({
        practiceId,
        clientId,
        enabled: canFetchEPrescribeData,
    });

    const clientAllergies = useGetClientAllergies({
        practiceId,
        clientId,
        enabled: canFetchEPrescribeData,
    });

    const clientPrescriptions = useGetPrescriptions({
        practiceId,
        filters: { client_id: clientId },
        enabled: canFetchEPrescribeData,
    });

    const triggerSSOLaunch = useTriggerSSOLaunch();
    const triggerClientSync = useTriggerClientSync();
    const isLoading = triggerClientSync.isLoading || triggerSSOLaunch.isLoading;

    if (!canViewEPrescribeFlow) return null;

    const handleTriggerClientSync = () => {
        triggerClientSync.mutate(
            {
                payload: {
                    session_history_id: noteDetails.session_history_id,
                    provider_id: noteDetails.provider.provider_id,
                },
                practiceId,
                clientId,
            },
            {
                onSuccess: (response) => {
                    toast({
                        mode: "success",
                        message: response.message ?? "Client sync successful",
                    });
                },
                onError: (error) => {
                    toast({
                        mode: "error",
                        message:
                            error.response?.data?.message ??
                            "Cannot sync client diagnosis at this time",
                    });
                },
            }
        );
    };

    const handleTriggerSSOLaunch = (
        landingScreen: SSOLandingScreens = "patient"
    ) => {
        triggerSSOLaunch.mutate(
            {
                practiceId,
                payload: {
                    session_history_id: noteDetails.session_history_id,
                    landing_screen: landingScreen,
                    client_id: clientId,
                },
            },
            {
                onSuccess: (response) => setLaunchData(response.data),
                onError: (error) => {
                    toast({
                        mode: "error",
                        message:
                            error.response?.data?.message ??
                            "Cannot launch e-prescribe screen at this time",
                    });
                },
            }
        );
    };

    return (
        <>
            <DrFirstIframe
                launchData={launchData}
                onClose={() => setLaunchData(null)}
                title="Update Client Medications/Allergies"
                clientId={clientId}
                practiceId={practiceId}
            />
            {showDiagnosisSync ? (
                <div className="mb-[--spacing-form-items] flex items-center  gap-x-[--spacing-items]">
                    <Button
                        disabled={isLoading}
                        onClick={handleTriggerClientSync}
                        variant="secondary"
                    >
                        Save Diagnosis Codes
                    </Button>
                    <div className="text-caption flex items-center gap-x-1 text-[--color-info-text] font-medium">
                        <InfoIcon />
                        Saves diagnosis codes to the e-prescribe platform.
                    </div>
                </div>
            ) : null}
            <div>
                <div className="mb-1 flex items-center gap-x-1 justify-between">
                    <h3 className="font-medium text-base">Allergies</h3>
                    <Button
                        variant="normal"
                        className="min-h-0 text-[13px] text-textLink ![border-bottom:1px_solid_var(--color-text-link)] !p-0 rounded-none hover:![border-bottom:1px_solid_transparent] gap-x-0.5"
                        onClick={() =>
                            handleTriggerSSOLaunch("manage_allergies")
                        }
                        disabled={
                            triggerClientSync.isLoading ||
                            triggerSSOLaunch.isLoading
                        }
                    >
                        Manage Allergies <ArrowUpRightIcon />
                    </Button>
                </div>
                <div className="mb-[--spacing-form-items]">
                    {clientAllergies.isLoading ? (
                        <Loader wrapperClassName="h-[73px]" />
                    ) : null}
                    {clientAllergies.error ? (
                        <ListState
                            isError
                            stateHelperText="Try reloading this page"
                            errorMsg={
                                clientAllergies.error?.response?.data
                                    ?.message ?? "Cannot display allergies"
                            }
                            context="general"
                            cardClass="h-[73px]"
                        />
                    ) : null}
                    {clientAllergies.isSuccess &&
                    clientAllergies.data.data.length === 0 ? (
                        <ListState
                            isError={false}
                            stateHelperText="Allergies will appear here when added"
                            emptyMessage="No allergies added"
                            context="general"
                            cardClass="h-[73px]"
                        />
                    ) : null}
                    {clientAllergies.isSuccess &&
                    clientAllergies.data.data.length > 0 ? (
                        <ClientAllergyListTable
                            data={clientAllergies.data.data}
                        />
                    ) : null}
                </div>
            </div>
            <div>
                <div className="mb-1 flex items-center gap-x-1 justify-between">
                    <h3 className="font-medium text-base">Medications</h3>
                    <Button
                        variant="normal"
                        className="min-h-0 text-[13px] text-textLink ![border-bottom:1px_solid_var(--color-text-link)] !p-0 rounded-none hover:![border-bottom:1px_solid_transparent] gap-x-0.5"
                        onClick={() =>
                            handleTriggerSSOLaunch("manage_medications")
                        }
                        disabled={
                            triggerClientSync.isLoading ||
                            triggerSSOLaunch.isLoading
                        }
                    >
                        Manage Medications <ArrowUpRightIcon />
                    </Button>
                </div>
                <div className="mb-[--spacing-form-items]">
                    {clientMedications.isLoading ? (
                        <Loader wrapperClassName="h-[73px]" />
                    ) : null}
                    {clientMedications.error ? (
                        <ListState
                            isError
                            stateHelperText="Try reloading this page"
                            errorMsg={
                                clientMedications.error?.response?.data
                                    ?.message ?? "Cannot display medications"
                            }
                            context="general"
                            cardClass="h-[73px]"
                        />
                    ) : null}
                    {clientMedications.isSuccess &&
                    clientMedications.data.data.length === 0 ? (
                        <ListState
                            isError={false}
                            stateHelperText="Medications will appear here when added"
                            emptyMessage="No medications added"
                            context="general"
                            cardClass="h-[73px]"
                        />
                    ) : null}
                    {clientMedications.isSuccess &&
                    clientMedications.data.data.length > 0 ? (
                        <ClientMedicationListTable
                            data={clientMedications.data.data}
                        />
                    ) : null}
                </div>
            </div>
            <div>
                <div className="mb-1 flex items-center gap-x-1 justify-between">
                    <h3 className="font-medium text-base">Prescriptions</h3>
                    <Button
                        variant="normal"
                        className="min-h-0 text-[13px] text-textLink ![border-bottom:1px_solid_var(--color-text-link)] !p-0 rounded-none hover:![border-bottom:1px_solid_transparent] gap-x-0.5"
                        onClick={() => handleTriggerSSOLaunch("report")}
                        disabled={
                            triggerClientSync.isLoading ||
                            triggerSSOLaunch.isLoading
                        }
                    >
                        Manage Prescriptions
                        <ArrowUpRightIcon />
                    </Button>
                </div>
                <div className="mb-[--spacing-form-items]">
                    {clientPrescriptions.isLoading ? (
                        <Loader wrapperClassName="h-[73px]" />
                    ) : null}
                    {clientPrescriptions.error ? (
                        <ListState
                            isError
                            stateHelperText="Try reloading this page"
                            errorMsg={
                                clientPrescriptions.error?.response?.data
                                    ?.message ?? "Cannot display prescriptions"
                            }
                            context="general"
                            cardClass="h-[73px]"
                        />
                    ) : null}
                    {clientPrescriptions.isSuccess &&
                    clientPrescriptions.data.data.length === 0 ? (
                        <ListState
                            isError={false}
                            stateHelperText="Prescriptions will appear here when added"
                            emptyMessage="No prescriptions added"
                            context="general"
                            cardClass="h-[73px]"
                        />
                    ) : null}
                    {clientPrescriptions.isSuccess &&
                    clientPrescriptions.data.data.length > 0 ? (
                        <PrescriptionListTable
                            data={clientPrescriptions.data.data}
                            hideNoteData
                        />
                    ) : null}
                </div>
            </div>
        </>
    );
}
